import { memo, useMemo } from 'react';
import type { ReaderHost, VerseRowModel } from './readerHost';

const asHtml = (html: string) => ({ __html: html });

export type RowState = 'counted' | 'confirmed' | 'undoing' | null;

interface VerseRowProps {
  verse: VerseRowModel;
  state: RowState;
  current: boolean;
  playing: boolean;
  host: ReaderHost;
}

/**
 * One verse exactly as the design suite's verseRow paints it: the rail (badge,
 * tick, redo), the side-by-side columns, and the word-by-word block when the
 * host supplied word chips. The four layouts are the same DOM; CSS on
 * .content switches between them.
 *
 * The markup objects are memoised on their strings: React re-sets
 * dangerouslySetInnerHTML whenever the object changes, which would rebuild
 * the word nodes on every re-render and drop the `.playing` / `.reciting`
 * classes the word audio toggles on them.
 */
export const VerseRow = memo(function VerseRow({ verse, state, current, playing, host }: VerseRowProps) {
  const icons = host.icons();
  const className = ['vrow', verse.words ? '' : 'nowords', state ?? '', current ? 'current' : '', playing ? 'aplay' : '']
    .filter(Boolean).join(' ');
  const tick = useMemo(() => asHtml(icons.tick), [icons.tick]);
  const undo = useMemo(() => asHtml(icons.undo), [icons.undo]);
  const ar = useMemo(() => asHtml(verse.ar), [verse.ar]);
  const tr = useMemo(() => asHtml(verse.tr), [verse.tr]);
  const en = useMemo(() => asHtml(verse.en), [verse.en]);
  const chips = useMemo(() => verse.words?.map((word, i) => (
    <div
      key={word.loc ?? i}
      className={'wtoken' + (word.y === 'end' ? ' end' : '') + (word.loc ? ' wplay' : '')}
      data-loc={word.loc}
    >
      <div className="wa" dangerouslySetInnerHTML={asHtml(word.a)} />
      {word.y === 'end' ? (
        <>
          <div className="wt" dangerouslySetInnerHTML={asHtml(host.numTranslit(verse.n))} />
          <div className="wm">{String(verse.n)}</div>
        </>
      ) : (
        <>
          {word.t && <div className="wt" dangerouslySetInnerHTML={asHtml(word.t)} />}
          <div className="wm" dangerouslySetInnerHTML={asHtml(word.m || '')} />
        </>
      )}
    </div>
  )), [verse.words, verse.n, host]);
  return (
    <div className={className} data-id={verse.id}>
      <div className="rail">
        <span className="vbadge">{verse.n}</span>
        <span className="tickwrap" dangerouslySetInnerHTML={tick} />
        <span className="redowrap" dangerouslySetInnerHTML={undo} />
      </div>
      <div className="cols">
        <div className="ar" dangerouslySetInnerHTML={ar} />
        <div className="enwrap">
          <div className="tr" dangerouslySetInnerHTML={tr} />
          <div className="en" dangerouslySetInnerHTML={en} />
        </div>
      </div>
      {chips && (
        <div className="wordblock">
          <div className="wordwrap">{chips}</div>
          <div className="fullen" dangerouslySetInnerHTML={en} />
        </div>
      )}
    </div>
  );
});

/** The gold surah band with the calligraphic name glyph (legacy band): frame svg, then the name. */
export function SurahBand({ surah, host }: { surah: number; host: ReaderHost }) {
  return (
    <div
      className="sband"
      dangerouslySetInnerHTML={asHtml(host.icons().bandFrame + '<div class="bname">' + host.surahNameGlyph(surah) + '</div>')}
    />
  );
}

/** The basmala row: a numberless verse row that never counts (legacy basmala). */
export function BasmalaRow({ host }: { host: ReaderHost }) {
  return (
    <div className="vrow bism nowords">
      <div className="rail" />
      <div className="cols">
        <div className="ar" dangerouslySetInnerHTML={asHtml(host.basmalaText())} />
        <div className="enwrap">
          <div className="tr">Bismi Allahi alrrahmani alrraheemi</div>
          <div className="en">In the name of Allah, the Entirely Merciful, the Especially Merciful.</div>
        </div>
      </div>
    </div>
  );
}
